import { useEffect, useState } from 'react';
import {
    Tree,
    getBackendOptions,
    MultiBackend,
    NodeModel,
} from '@minoru/react-dnd-treeview';
import { DndProvider } from 'react-dnd';
import styles from './CustomTree.module.css';
import { CustomNode } from './CustomNode';
import { Props } from './GroupedEmployeeTreeProps';
import { EmployeeNodeModel } from '../../../../models';

const isSameNode = (a: EmployeeNodeModel, b: EmployeeNodeModel) =>
    String(a.id) === String(b.id);

const isNodeInList = (node: EmployeeNodeModel, list: EmployeeNodeModel[]) =>
    list.some((n) => isSameNode(n, node));

/**
 * Este componente tiene toda la logica del renderizado del arbol, cuando se muestra y todo el tema del seleccionado de cada nodo
 * @param onSelectionChange (selectedEmployees: EmployeeNodeModel[]) => void
 * @param employees EmployeeNodeModel[]
 * @param isLoading boolean
 * @param isSelectAllEmployes boolean
 * @param employeesFromTemplate EmployeeNodeModel[]
 * @param searchValue string
 * @param typeOfPerson string
 * @returns Devuelve el contenedor con el arbol de los empleados y la logica relaccionada a el
 */
export default function GroupedEmployeesTree({
    onSelectionChange,
    employees,
    isLoading,
    isSelectAllEmployes,
    employeesFromTemplate,
    searchValue,
    typeOfPerson,
    contractorSelected,
}: Props) {
    const [selectedEmployees, setSelectedEmployees] = useState<EmployeeNodeModel[]>(
        []
    );

    const [selectedEmployeesToDelete, setSelectedEmployeesToDelete] = useState<
        EmployeeNodeModel[]
    >([]);

    const uniqueEmployees = Array.from(
        new Map(employees.map(emp => [emp.id, emp])).values()
    );

    useEffect(() => {
        if (isSelectAllEmployes) {
            let currentlySelectedEmployees = [...selectedEmployees];

            uniqueEmployees.map((employee) => {
                if (!isNodeInList(employee, currentlySelectedEmployees)) {
                    currentlySelectedEmployees.push(employee);
                }
            });

            onSelectionChange(currentlySelectedEmployees);
            setSelectedEmployees(currentlySelectedEmployees);
        }
        if (!isSelectAllEmployes) {
            onSelectionChange([]);
            setSelectedEmployees([]);
        }
    }, [isSelectAllEmployes]);

    useEffect(() => {
        if (employeesFromTemplate.length > 0) {
            onSelectionChange(employeesFromTemplate);
            setSelectedEmployees(employeesFromTemplate);
        } else {
            setSelectedEmployees([]);
            onSelectionChange([]);
        }
    }, [employeesFromTemplate]);

    useEffect(() => {
        setSelectedEmployees([]);
        setSelectedEmployeesToDelete([]);
    }, [typeOfPerson]);

    useEffect(() => {
        setSelectedEmployees([]);
        setSelectedEmployeesToDelete([]);
    }, [contractorSelected]);

    const handlerSelectedNode = (node: EmployeeNodeModel) => {
        validateItem(node);
    };

    const validateItem = (selectedNode: EmployeeNodeModel) => {
        let currentlySelectedEmployees = [...selectedEmployees];

        const item = currentlySelectedEmployees.find(
            (n) => String(n.id) === String(selectedNode.id)
        );

        if (!item) {
            setSelectedEmployeesToDelete([]);

            if (!selectedNode.documentNumber) {
                let childNodes = employees.filter(
                    (node) => String(node.parent) === String(selectedNode.id)
                );

                currentlySelectedEmployees.push(selectedNode);

                if (childNodes.length > 0) {
                    childNodes.forEach((node) => {
                        if (!node.documentNumber) {
                            validateItem(node);
                        } else {
                            currentlySelectedEmployees.push(node);
                        }
                    });
                }
            } else {
                currentlySelectedEmployees.push(selectedNode);
            }

            setSelectedEmployees((prev) => {
                return [
                    ...prev,
                    ...currentlySelectedEmployees.filter(
                        (current) => !isNodeInList(current, prev)
                    ),
                ];
            });
        } else {
            let deleteList = [...selectedEmployeesToDelete];

            if (!selectedNode.documentNumber) {
                let childNodes = selectedEmployees.filter(
                    (node) => String(node.parent) === String(selectedNode.id)
                );

                deleteList.push(selectedNode);

                if (childNodes.length > 0) {
                    childNodes.forEach((node) => {
                        if (!node.documentNumber) {
                            validateItem(node);
                        } else {
                            deleteList.push(node);
                        }
                    });
                }
            } else {
                deleteList.push(selectedNode);
            }

            setSelectedEmployeesToDelete((prev) => {
                return [
                    ...prev,
                    ...deleteList.filter((current) => !isNodeInList(current, prev)),
                ];
            });
        }
    };

    const handleDrop = () => {};

    useEffect(() => {
        onSelectionChange(selectedEmployees);
    }, [selectedEmployees]);

    useEffect(() => {
        setSelectedEmployees((prev) => {
            return [
                ...prev.filter(
                    (current) => !isNodeInList(current, selectedEmployeesToDelete)
                ),
            ];
        });
    }, [selectedEmployeesToDelete]);

    return (
        <div className='no-listing'>
            {!isLoading ? (
                <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                    <Tree
                        sort={false}
                        tree={uniqueEmployees}
                        rootId={0}
                        render={(node: NodeModel, { depth, isOpen, onToggle }) => (
                            <CustomNode
                                node={node}
                                depth={depth}
                                isOpen={isOpen}
                                isSelected={isNodeInList(node as EmployeeNodeModel, selectedEmployees)}
                                onToggle={onToggle}
                                onSelect={handlerSelectedNode}
                                searchValue={searchValue}
                            />
                        )}
                        onDrop={handleDrop}
                    />
                </DndProvider>
            ) : (
                <div className='card' aria-hidden='true'>
                    <h5 className='card-title placeholder-glow'>
                        <span className='placeholder col-6'></span>
                    </h5>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-7 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-7 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-8 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-8 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-7 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-8 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-7 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-8 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                    <p className='card-text placeholder-glow'>
                        <span className='placeholder col-7 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                        <span className='placeholder col-6 bg-dark'></span>
                        <span className='placeholder col-8 bg-dark'></span>
                        <span className='placeholder col-4 bg-dark'></span>
                    </p>
                </div>
            )}
        </div>
    );
}
